import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, RotateCcw, X } from 'lucide-react'
import { useUpdateStatus } from '../../../hooks/useUpdateStatus'
import imgAvailable from '../../../assets/images/updater/updateAvailable@2x.png'
import imgDownloading from '../../../assets/images/updater/updateDownloading@2x.png'
import imgReady from '../../../assets/images/updater/updateReady@2x.png'
import '../../ui/Button/Button.scss'
import './UpdateSidebarOverlay.scss'

interface UpdateSidebarOverlayProps {
  sidebarIsOpen: boolean
}

export function UpdateSidebarOverlay({ sidebarIsOpen }: UpdateSidebarOverlayProps) {
  const { t } = useTranslation()
  const { state, download, installAndRestart, dismiss } = useUpdateStatus()

  const isActive = state.status === 'available' || state.status === 'downloading' || state.status === 'ready'
  const [shouldRender, setShouldRender] = useState(isActive)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    if (isActive) {
      setShouldRender(true)
      setIsLeaving(false)
    } else if (shouldRender) {
      setIsLeaving(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsLeaving(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [isActive, shouldRender])

  // Snapshot the last active state so the fade-out animation renders
  // the correct content even after status resets to idle/error.
  const lastActiveStateRef = useRef(state)
  if (isActive) lastActiveStateRef.current = state
  const displayState = isLeaving ? lastActiveStateRef.current : state
  const {
    status: displayStatus,
    newVersion: displayNewVersion,
    currentVersion: displayCurrentVersion,
    downloadPercent: displayDownloadPercent,
  } = displayState

  const iconSrc = displayStatus === 'available' ? imgAvailable
    : displayStatus === 'downloading' ? imgDownloading
    : displayStatus === 'ready' ? imgReady
    : imgAvailable

  if (!shouldRender) return null

  const percent = Math.round(displayDownloadPercent)

  const wrapperClass = [
    'update-sidebar-overlay',
    !sidebarIsOpen ? 'update-sidebar-overlay--collapsed' : '',
    isLeaving ? 'update-sidebar-overlay--leaving' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClass}>
      <div className="update-sidebar-overlay__card">
        {displayStatus !== 'downloading' && (
          <button
            className="update-sidebar-overlay__close"
            onClick={dismiss}
            aria-label={t('updater.close')}
          >
            <X size={14} />
          </button>
        )}

        <div className="update-sidebar-overlay__row">
          <img
            src={iconSrc}
            width={48}
            height={48}
            alt=""
            className={`update-sidebar-overlay__icon${displayStatus === 'downloading' ? ' update-sidebar-overlay__icon--driving' : ''}`}
          />
          <div className="update-sidebar-overlay__body">
            <p className="update-sidebar-overlay__title">
              {displayStatus === 'available' && t('updater.overlayTitle.available')}
              {displayStatus === 'downloading' && t('updater.overlayTitle.downloading')}
              {displayStatus === 'ready' && t('updater.overlayTitle.ready')}
            </p>
            <p className="update-sidebar-overlay__subtitle">
              {displayStatus === 'available' && t('updater.overlayDetail.available', { newVersion: displayNewVersion, currentVersion: displayCurrentVersion })}
              {displayStatus === 'downloading' && t('updater.overlayDetail.downloading', { newVersion: displayNewVersion, currentVersion: displayCurrentVersion })}
              {displayStatus === 'ready' && t('updater.overlayDetail.ready', { newVersion: displayNewVersion })}
            </p>
          </div>
        </div>

        {displayStatus === 'downloading' && (
          <div className="update-sidebar-overlay__progress">
            <div className="update-sidebar-overlay__track">
              <div
                className="update-sidebar-overlay__bar"
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="update-sidebar-overlay__percent">{percent}%</span>
          </div>
        )}

        {displayStatus === 'available' && (
          <button
            type="button"
            className="button button--outline button--size-default update-sidebar-overlay__action"
            onClick={download}
          >
            <span className="button__icon"><Download size={14} /></span>
            <span className="button__text">{t('updater.overlayAction.download')}</span>
          </button>
        )}

        {displayStatus === 'ready' && (
          <button
            type="button"
            className="button button--primary button--size-default update-sidebar-overlay__action update-sidebar-overlay__action--restart"
            onClick={installAndRestart}
          >
            <span className="button__icon"><RotateCcw size={14} /></span>
            <span className="button__text">{t('updater.overlayAction.restart')}</span>
          </button>
        )}
      </div>
    </div>
  )
}
